import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ReturnPolicyPage() {
  return (
    <div data-testid="return-policy-page">
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-6">
              <RotateCcw className="text-[#A67A5B]" size={20} />
              <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B]">Our Commitment</p>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-4 leading-tight">
              Return &amp; Refund Policy
            </h1>
            <p className="font-sans text-sm text-[#7A736E] mb-12">Last updated: April 2026</p>

            <div className="space-y-10 font-sans text-[#2A2421]/80 leading-relaxed">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Our Promise</h2>
                <p className="text-base">
                  We want you to be completely satisfied with your purchase from Led By Faith Studios. If something is not right, we are here to help. Please review the following policy to understand how returns and refunds are handled.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Shopify Store Purchases</h2>
                <p className="text-base mb-3">
                  For items purchased through our Shopify store:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#7A736E]">
                  <li>Returns are accepted within <strong>14 days</strong> of delivery</li>
                  <li>Items must be in original, unused condition</li>
                  <li>Return shipping costs are the responsibility of the buyer unless the item arrived damaged or defective</li>
                  <li>Refunds will be processed to the original payment method within 5&ndash;10 business days of receiving the returned item</li>
                </ul>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Amazon Purchases</h2>
                <p className="text-base">
                  Products purchased through Amazon are subject to Amazon's return and refund policies. Please visit Amazon's Help &amp; Customer Service page or contact Amazon directly for return requests on Amazon orders.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Damaged or Defective Items</h2>
                <p className="text-base">
                  If you receive a damaged or defective item, please contact us within 7 days of delivery with a photo of the issue. We will arrange a replacement or full refund at no additional cost to you.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Digital Products</h2>
                <p className="text-base">
                  Due to the nature of digital products, all digital purchases are considered final sale and are not eligible for returns or refunds unless there is a technical issue preventing access.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Exchanges</h2>
                <p className="text-base">
                  If you would like to exchange an item, please contact us and we will do our best to accommodate your request, subject to availability.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">How to Request a Return</h2>
                <p className="text-base mb-3">To initiate a return or report an issue:</p>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#7A736E]">
                  <li>
                    <Link to="/contact?inquiry=return_policy" data-testid="return-initiate-link" className="text-[#A67A5B] hover:text-[#8C654A] font-medium transition-colors underline">
                      Contact us here
                    </Link> with your order number and reason for the return
                  </li>
                  <li>Include a photo if the item is damaged or defective</li>
                  <li>We will respond within 1&ndash;2 business days with return instructions</li>
                </ul>
              </div>

              <div className="pt-6 border-t border-[#E8E1D9]">
                <h2 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">Need Help?</h2>
                <p className="text-base mb-4">
                  Your satisfaction matters to us. If you have any questions or concerns, please do not hesitate to reach out.
                </p>
                <Link
                  to="/contact?inquiry=return_policy"
                  data-testid="return-contact-link"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#A67A5B] hover:text-[#8C654A] transition-colors"
                >
                  Contact us here &rarr;
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
